import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';

import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useNeedsEmailVerification } from '../../../hooks/useNeedsEmailVerification';
import { usePortalName } from '../../../hooks/usePortalName';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';

import {
  fetchLoggedInUserInfoAsync,
  selectEmailVerificationSuccess,
  selectLoggedInUserInfo,
  setEmailVerificationSuccess,
  setLoggedInUserInitialized,
} from '../../../data/user.slice';
import { setSuccessMessage } from '../../../data/ui.slice';
import userService from '../../../services/userService';

import VerifyEmailPageForm from '../../../components/member/auth/VerifyEmailPageForm';

const PREFIX = 'MemberVerifyEmail';

const classes = {
  container: `${PREFIX}-container`,
  left: `${PREFIX}-left`,
  right: `${PREFIX}-right`,
  center: `${PREFIX}-center`,
  logo: `${PREFIX}-logo`,
  formContainer: `${PREFIX}-formContainer`,
};

const portalName = usePortalName();
const heroImage = `${portalName}/auth_hero.webp`;
const logoSrc = `${portalName}/brand-logo-primary.svg`;

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: '100vh',
    backgroundSize: 'cover',
    backgroundImage: `url(${heroImage})`,
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  [`& .${classes.left}`]: {
    flex: 1,
    display: 'flex',
    alignItems: 'start',
    justifyContent: 'center',
    paddingLeft: theme.spacing(10),
    paddingTop: theme.spacing(30),
    [theme.breakpoints.down('md')]: {
      paddingLeft: theme.spacing(2),
      justifyContent: 'center',
    },
  },
  [`& .${classes.right}`]: {
    flex: 1,
    display: 'flex',
    alignItems: 'start',
    justifyContent: 'center',
    paddingRight: theme.spacing(10),
    paddingTop: theme.spacing(11),
    paddingBottom: theme.spacing(3),
    overflowY: 'auto',
    [theme.breakpoints.down('md')]: {
      paddingRight: theme.spacing(2),
      justifyContent: 'center',
    },
  },
  [`& .${classes.center}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    height: '100vh',
    overflowY: 'auto',
    padding: theme.spacing(4),
    gap: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2),
    },
  },
  [`& .${classes.logo}`]: {
    width: 'auto',
    height: 'auto',
    display: 'block',
    objectFit: 'contain',
    [theme.breakpoints.down('md')]: {
      maxWidth: 180,
      marginBottom: theme.spacing(4),
      marginTop: theme.spacing(4),
    },
  },
  [`& .${classes.formContainer}`]: {
    position: 'relative',
    maxWidth: 375,
    width: '100%',
  },
}));

export default function MemberVerifyEmail() {
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('md'));
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const [errorMessage, setErrorMessage] = useState<string>();
  const needsEmailVerification = useNeedsEmailVerification();
  const userInfo = useAppSelector(selectLoggedInUserInfo);
  const [searchParams] = useSearchParams();
  const VERIFICATION_TOKEN_PARAM = 'VERIFICATION_TOKEN';
  const token = searchParams.get(VERIFICATION_TOKEN_PARAM);
  const navigate = useNavigate();
  const [tokenInvalid, setTokenInvalid] = useState(false);

  useEffect(() => {
    if (!needsEmailVerification || !userInfo) {
      navigate('/');
    }
  }, [needsEmailVerification, userInfo]);

  useEffect(() => {
    if (token) {
      userService.verifyEmail(token).subscribe({
        next: () => {
          dispatch(setLoggedInUserInitialized(false));
          dispatch(fetchLoggedInUserInfoAsync());
          dispatch(setEmailVerificationSuccess(true));
          navigate('/');
        },
        error: (err) => {
          setTokenInvalid(true);
          setErrorMessage(t('member.invalidEmailVerificationToken'));
          if (!userInfo) {
            navigate('/');
          }
        },
      });
    } else {
      setTokenInvalid(true);
    }
  }, [token]);

  const emailVerificationSuccess = useAppSelector(
    selectEmailVerificationSuccess,
  );

  useEffect(() => {
    if (emailVerificationSuccess) {
      dispatch(setSuccessMessage(t('member.emailVerified')));
      dispatch(setEmailVerificationSuccess(false));
    }
  }, [emailVerificationSuccess]);

  if (!tokenInvalid) {
    return null;
  }

  return (
    <Root className={classes.container}>
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        {isLarge && (
          <Box className={classes.left}>
            <img
              alt={`${t('member.logoAlt')}`}
              src={logoSrc}
              className={classes.logo}
            />
          </Box>
        )}
        {isLarge && (
          <Box className={classes.right}>
            <Box className={classes.formContainer}>
              <VerifyEmailPageForm error={errorMessage} />
            </Box>
          </Box>
        )}
        {!isLarge && (
          <Box className={classes.center}>
            <img
              alt={`${t('member.logoAlt')}`}
              src={logoSrc}
              className={classes.logo}
            />
            <Box className={classes.formContainer}>
              <VerifyEmailPageForm error={errorMessage} />
            </Box>
          </Box>
        )}
      </LocalizationProvider>
    </Root>
  );
}
